import * as QueryKeys from "data";
import { Alert, Card, CardContent, Skeleton } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useState } from "react";
import { AppTotal } from "contexts/models";
import { useIdentity } from "contexts/identity-context";
import dayjs from "dayjs";
import { getActiveOrganisation } from "contexts/identity-context";
import { fetchApiKeys, fetchUsage } from "data/queries";
import { useQuery } from "react-query";
import { resolveIdentityId } from "utils/identity";
import CustomTable from "components/shared/table";

const totalOrZero = (v: AppTotal | undefined) => {
  if (v !== undefined) {
    return Number(v.totalCredits.toFixed(1));
  }
  return 0;
};

const buildRowData = (apiKeysQuery: any, usageQuery: any) => {
  let keyNameMapping: any = {};
  apiKeysQuery.data.forEach(function (key: any) {
    const keyName: string = key.name;
    const keyDisplayName: string = key.displayName ? key.displayName : key.name;
    keyNameMapping[keyName] = keyDisplayName;
  });

  const rows: any = [];

  usageQuery.data?.appProductTotals.forEach((app: any, index: any) => {
    if (app.totalUsage.length === 0 && keyNameMapping[app.name] === undefined) {
      return;
    }

    rows.push({
      id: app.name,
      keyName:
        keyNameMapping[app.name] === undefined
          ? `${app.name} - DELETED`
          : keyNameMapping[app.name],
      predictive: totalOrZero(
        app.totalUsage.find((x: any) => x.product.includes("predictive"))
      ),
      addresses: totalOrZero(
        app.totalUsage.find((x: any) => x.product.includes("addresses"))
      ),
      landparcels: totalOrZero(
        app.totalUsage.find((x: any) => x.product.includes("land-parcels"))
      ),
      buildings: totalOrZero(
        app.totalUsage.find((x: any) => x.product.includes("buildings"))
      ),
      batches: totalOrZero(
        app.totalUsage.find((x: any) => x.product.includes("batches"))
      ),
      adminbounds: totalOrZero(
        app.totalUsage.find((x: any) =>
          x.product.includes("administrative-boundaries")
        )
      ),
      maps: totalOrZero(
        app.totalUsage.find((x: any) => x.product.includes("maps"))
      ),
      esri: totalOrZero(
        app.totalUsage.find((x: any) =>
          x.product.includes("esri-compatible-locator")
        )
      ),
    });
  });

  return rows;
};

export const UsageTable = () => {
  const [identityState] = useIdentity();
  const isUser = !getActiveOrganisation(identityState);
  const identityId = resolveIdentityId(identityState, isUser);

  const startTime = dayjs().subtract(1, "week").unix() * 1000;
  const endTime = dayjs().unix() * 1000;

  const usageQuery = useQuery([QueryKeys.usageGraph, identityId], () =>
    fetchUsage(isUser, identityState, startTime, endTime)
  );
  const apiKeysQuery = useQuery([QueryKeys.apiKeys, identityId], () =>
    fetchApiKeys(isUser, identityState)
  );

  const isLoading: boolean = usageQuery.isLoading || apiKeysQuery.isLoading;
  const isError: boolean = usageQuery.isError || apiKeysQuery.isError;
  const isSuccess: boolean = usageQuery.isSuccess && apiKeysQuery.isSuccess;
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const cols: GridColDef[] = [
    { field: "keyName", headerName: "API Key", width: 250 },
    { field: "addresses", headerName: "Addresses API", width: 110 },

    { field: "buildings", headerName: "Buildings API", width: 110 },
    { field: "batches", headerName: "Batches API", width: 110 },
    { field: "maps", headerName: "Maps API", width: 110 },
    { field: "landparcels", headerName: "Land Parcels API", width: 110 },
    { field: "predictive", headerName: "Predictive API", width: 110 },
    { field: "esri", headerName: "Esri Compatible Locator", width: 110 },
    {
      field: "adminbounds",
      headerName: "Administrative Boundaries API",
      width: 150,
    },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        marginBottom: "5%",
      }}
    >
      {isError && (
        <Alert severity="error">
          An error occurred while fetching the usage data.
        </Alert>
      )}

      {isLoading && (
        <Card>
          <CardContent>
            <Skeleton variant="rectangular" width="100%" height="300px" />
          </CardContent>
        </Card>
      )}

      {isSuccess && (
        <Card>
          <CustomTable
            rows={buildRowData(apiKeysQuery, usageQuery)}
            cols={cols}
            isLoading={isLoading}
            overlayText={"No usage data"}
          />
        </Card>
      )}
    </div>
  );
};
